import { Controller } from "@hotwired/stimulus"

// A number answer is stored plain — digits, an optional minus and a dot decimal —
// and only ever shown formatted. So the visible field carries the number with the
// separators the survey author picked, and the companion field beside it carries
// the plain value that is actually submitted.
export default class extends Controller {
  static targets = ["input", "value"]
  static values = {
    decimalSeparator: { type: String, default: "." },
    thousandsSeparator: { type: String, default: "" }
  }

  connect() {
    this.reformat()
  }

  reformat() {
    const input = this.inputTarget
    const digitsBeforeCaret = this.digitsBefore(input.value, input.selectionStart)
    const formatted = this.formatted(input.value)

    if (this.hasValueTarget) this.valueTarget.value = this.plain(formatted)
    if (formatted === input.value) return

    input.value = formatted
    if (digitsBeforeCaret === null || document.activeElement !== input) return

    // Re-anchor the caret to the digit it sat behind: inserting a separator
    // ahead of it would otherwise drop the caret at the end of the field.
    const caret = this.caretAfter(formatted, digitsBeforeCaret)
    input.setSelectionRange(caret, caret)
  }

  formatted(rawValue) {
    const { negative, integerDigits, decimals } = this.parts(rawValue)
    const grouped = this.grouped(integerDigits)
    const decimalPart = decimals === null ? "" : this.decimalSeparatorValue + decimals
    if (grouped === "" && decimalPart === "") return negative ? "-" : ""

    return `${negative ? "-" : ""}${grouped}${decimalPart}`
  }

  // What the server stores: no grouping, and a dot decimal whatever the question
  // displays, so the answer stays a number every reader can parse.
  plain(displayValue) {
    const { negative, integerDigits, decimals } = this.parts(displayValue)
    if (integerDigits === "" && !decimals) return ""

    const decimalPart = decimals ? `.${decimals}` : ""

    return `${negative ? "-" : ""}${integerDigits}${decimalPart}`
  }

  parts(rawValue) {
    const decimal = this.decimalSeparatorValue || "."
    const kept = rawValue.replace(new RegExp(`[^0-9${this.escaped(decimal)}]`, "g"), "")
    const [integerDigits, ...decimalParts] = kept.split(decimal)

    return {
      negative: rawValue.trim().startsWith("-"),
      integerDigits,
      decimals: decimalParts.length > 0 ? decimalParts.join("") : null
    }
  }

  // The two separators can be configured to the same character, and grouping
  // with it would make the value unreadable — so it yields to the decimal one.
  grouped(integerDigits) {
    const separator = this.thousandsSeparatorValue
    if (!separator || separator === this.decimalSeparatorValue) return integerDigits

    return integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  }

  digitsBefore(value, caretPosition) {
    if (caretPosition === null || caretPosition === undefined) return null

    return value.slice(0, caretPosition).replace(/\D/g, "").length
  }

  caretAfter(value, digitCount) {
    if (digitCount === 0) return value.search(/\d/) === -1 ? value.length : value.search(/\d/)

    let seen = 0

    for (let index = 0; index < value.length; index += 1) {
      if (/\d/.test(value[index])) seen += 1
      if (seen === digitCount) return index + 1
    }

    return value.length
  }

  escaped(character) {
    return character.replace(/[.*+?^${}()|[\]\\-]/g, "\\$&")
  }
}
